// src/api/conversationsApi.ts

import { baseApi } from './baseApi';
import type {
  ConversationSession,
  ConversationDetailResponse,
  CreateConversationRequest,
  CreateConversationResponse,
  AddMessageRequest,
  AddMessageResponse,
} from '../types/api';

export const conversationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversations: builder.query<ConversationSession[], string | void>({
      query: (workspaceId) => ({
        url: '/conversations',
        params: workspaceId ? { workspaceId } : undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'Conversation' as const,
                id,
              })),
              { type: 'Conversation', id: 'LIST' },
            ]
          : [{ type: 'Conversation', id: 'LIST' }],
    }),

    getConversationDetail: builder.query<ConversationDetailResponse, string>({
      query: (sessionId) => `/conversations/${sessionId}`,
      providesTags: (result, error, sessionId) => [
        { type: 'Conversation', id: sessionId },
      ],
    }),

    createConversation: builder.mutation<CreateConversationResponse, CreateConversationRequest>({
      query: (body) => ({
        url: '/conversations',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Conversation', id: 'LIST' }],
    }),

    addMessage: builder.mutation<AddMessageResponse, { sessionId: string; content: string }>({
      query: ({ sessionId, content }) => ({
        url: `/conversations/${sessionId}/messages`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'Conversation', id: sessionId },
      ],
    }),

    // WeOpen sessions - for future use
    getWeopenSessions: builder.query<ConversationSession[], { workspaceId: string; status?: string }>({
      query: ({ workspaceId, status }) => ({
        url: '/channels/weopen/sessions',
        params: { workspaceId, ...(status && { status }) },
      }),
      providesTags: [{ type: 'Conversation', id: 'WEOPEN' }],
    }),

    sendWeopenMessage: builder.mutation<void, { sessionId: string; content: string }>({
      query: ({ sessionId, content }) => ({
        url: `/channels/weopen/${sessionId}/send`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'Conversation', id: sessionId },
      ],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationDetailQuery,
  useCreateConversationMutation,
  useAddMessageMutation,
  useGetWeopenSessionsQuery,
  useSendWeopenMessageMutation,
} = conversationsApi;
